import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useAppContext } from '~/app-context'
import { LoadingIndicator } from '~common/components/loading'
import styled from 'styled-components'
import { httpsCallable } from 'firebase/functions'
import { PluginId } from '~/features/plugins/config'

export const QontoAuthCallback: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { services, storageModules } = useAppContext()
    const [status, setStatus] = useState<'processing' | 'error' | 'success'>(
        'processing',
    )
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    useEffect(() => {
        const processAuth = async () => {
            const params = new URLSearchParams(location.search)
            const code = params.get('code')
            const state = params.get('state')
            const error = params.get('error')

            if (error) {
                setStatus('error')
                setErrorMessage(`Qonto auth error: ${error}`)
                return
            }

            if (!code) {
                setStatus('error')
                setErrorMessage('No authorization code received')
                return
            }

            const storedState = localStorage.getItem('qonto_auth_state')
            if (state !== storedState) {
                setStatus('error')
                setErrorMessage('Invalid state parameter')
                return
            }
            localStorage.removeItem('qonto_auth_state')

            try {
                const response =
                    await services.cloudFunctions.exchangeQontoToken({
                        code,
                        redirectUri: window.location.origin + '/qonto-auth',
                    })

                if (response.refreshToken) {
                    setStatus('success')

                    if (window.opener) {
                        window.opener.postMessage('qonto-auth-success', '*')
                        window.close()
                    } else {
                        setTimeout(
                            () => navigate('/settings?section=plugin-settings'),
                            1500,
                        )
                    }
                }
            } catch (err) {
                console.error('Qonto auth failed', err)
                setStatus('error')
                setErrorMessage(
                    err instanceof Error
                        ? err.message
                        : 'Authentication failed',
                )
            }
        }

        processAuth()
    }, [location, navigate, services, storageModules])

    if (status === 'error') {
        return (
            <Container>
                <Title>Authentication Failed</Title>
                <ErrorMessage>{errorMessage}</ErrorMessage>
                <Button
                    onClick={() =>
                        navigate('/settings?section=plugin-settings')
                    }
                >
                    Back to Settings
                </Button>
            </Container>
        )
    }

    if (status === 'success') {
        return (
            <Container>
                <Title>Successfully Connected to Qonto</Title>
                <LoadingIndicator size="medium" />
                <Message>Redirecting back to settings...</Message>
            </Container>
        )
    }

    return (
        <Container>
            <Title>Connecting to Qonto...</Title>
            <LoadingIndicator size="medium" />
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    gap: 20px;
`

const Title = styled.h2`
    font-size: 24px;
    color: ${({ theme }) => theme.colors.text.primary};
`

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.colors.error};
    margin-bottom: 20px;
`

const Message = styled.div`
    color: ${({ theme }) => theme.colors.text.secondary};
`

const Button = styled.button`
    padding: 10px 20px;
    background-color: ${({ theme }) => theme.colors.text.primary};
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
`
